import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { useRefreshTick } from "@/lib/autoRefresh";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PiggyBank, ArrowRight } from "lucide-react";

function TunaiPemohonCard({ s, list, clickable }) {
  const [open, setOpen] = useState(false);
  const body = (
    <>
      <p className="text-xs text-amber-800 font-semibold">Tunai Pemohon</p>
      <p className="font-mono font-bold text-amber-700" data-testid="deposit-sum-tunai-pemohon">{formatRupiah(s.tunai_pemohon || 0)}</p>
      <p className="text-[10px] text-muted-foreground">{clickable ? `${list.length} Pemohon · klik untuk rincian` : "Masih dipegang Pemohon"}</p>
    </>
  );
  if (!clickable) return <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">{body}</div>;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="text-left rounded-lg bg-amber-50 border border-amber-200 p-3 hover:bg-amber-100 hover:border-amber-300 transition-colors" data-testid="deposit-sum-tunai-pemohon-btn">{body}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tunai-pemohon-dialog">
          <DialogHeader>
            <DialogTitle>Tunai Pemohon</DialogTitle>
            <DialogDescription>Kas tunai yang masih dipegang setiap Pemohon (belum disetor + setoran tunai menunggu konfirmasi).</DialogDescription>
          </DialogHeader>
          {list.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6" data-testid="tunai-pemohon-empty">Tidak ada kas tunai yang dipegang Pemohon.</p> : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm" data-testid="tunai-pemohon-table">
                <thead className="bg-secondary text-xs text-muted-foreground"><tr>
                  <th className="text-left px-3 py-2">Pemohon</th><th className="text-right px-3 py-2">Belum Disetor</th>
                  <th className="text-right px-3 py-2">Menunggu Konfirmasi</th><th className="text-right px-3 py-2">Total Dipegang</th></tr></thead>
                <tbody>
                  {list.map((r) => (
                    <tr key={r.owner_id} className="border-t" data-testid={`tunai-pemohon-row-${r.owner_id}`}>
                      <td className="px-3 py-2">{r.name}<span className="block text-[11px] text-muted-foreground">{r.branch_name}</span></td>
                      <td className="px-3 py-2 text-right font-mono text-rose-600">{formatRupiah(r.not_deposited)}</td>
                      <td className="px-3 py-2 text-right font-mono text-amber-700">{formatRupiah(r.pending)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold" data-testid={`tunai-pemohon-held-${r.owner_id}`}>{formatRupiah(r.held)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t bg-secondary/50 font-semibold"><tr>
                  <td className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-right font-mono">{formatRupiah(list.reduce((t, r) => t + r.not_deposited, 0))}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatRupiah(list.reduce((t, r) => t + r.pending, 0))}</td>
                  <td className="px-3 py-2 text-right font-mono" data-testid="tunai-pemohon-total">{formatRupiah(list.reduce((t, r) => t + r.held, 0))}</td>
                </tr></tfoot>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function DepositSummaryCards({ s, held, isPemohon }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      <div className="rounded-lg bg-secondary p-3"><p className="text-xs text-muted-foreground">Total Dana Masuk</p><p className="font-mono font-bold" data-testid="deposit-sum-total">{formatRupiah((s.tunai_pusat || 0) + (s.transfer || 0))}</p><p className="text-[10px] text-muted-foreground">Tunai Pusat + Transfer masuk · {s.count || 0} setoran</p></div>
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3"><p className="text-xs text-emerald-800 font-semibold">Tunai Pusat</p><p className="font-mono font-bold text-emerald-700" data-testid="deposit-sum-tunai-pusat">{formatRupiah(s.tunai_pusat || 0)}</p><p className="text-[10px] text-muted-foreground">{isPemohon ? "Setoran tunai diterima" : "Saldo kas pusat saat ini (setelah pencairan & setor rekening)"}</p></div>
      <TunaiPemohonCard s={s} list={held} clickable={!isPemohon} />
      <div className="rounded-lg bg-secondary p-3"><p className="text-xs text-muted-foreground">Transfer (Masuk Rekening)</p><p className="font-mono font-bold" data-testid="deposit-sum-transfer">{formatRupiah(s.transfer || 0)}</p><p className="text-[10px] text-muted-foreground" data-testid="deposit-sum-transfer-detail">Transaksi {formatRupiah(s.transfer_sales || 0)} · Setoran {formatRupiah(s.transfer_deposit || 0)}</p></div>
      <div className="rounded-lg bg-secondary p-3"><p className="text-xs text-muted-foreground">Menunggu Konfirmasi</p><p className="font-mono font-bold text-amber-700" data-testid="deposit-sum-pending">{s.pending || 0}</p></div>
    </div>
  );
}

export function DashboardDepositSummary({ branch }) {
  const [data, setData] = useState(null);
  const tick = useRefreshTick();
  useEffect(() => {
    api.get("/cash-deposits", { params: branch && branch !== "all" ? { branch_id: branch } : {} })
      .then((r) => setData(r.data)).catch(() => setData({ summary: {}, pemohon_held: [] }));
  }, [branch, tick]);
  return (
    <Card className="p-4" data-testid="dashboard-deposit-summary">
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="font-semibold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><PiggyBank className="h-5 w-5 text-brand" /> Setor Kas</p>
        <Link to="/cash-deposits" className="text-xs text-brand font-medium inline-flex items-center gap-1 hover:underline" data-testid="dashboard-deposit-link">Lihat Setor Kas <ArrowRight className="h-3 w-3" /></Link>
      </div>
      {!data ? <div className="h-16 rounded-md skeleton-shimmer" /> : <DepositSummaryCards s={data.summary || {}} held={data.pemohon_held || []} isPemohon={false} />}
    </Card>
  );
}
